import axios from "axios";
import { CustomerDetails } from "../features/customerSlice";
import { Booking, PidClass, ShipmentType } from "../features/bookingSlice";
import { ApiConfig, appDataActions, PidEurShipmentValue } from "../features/appDataSlice";
import { AppDispatch } from "../app/store";

export enum TradeRoute {
  DOMESTIC = 'domestic',
  EXPORT = 'export',
  IMPORT = 'import',
  CROSSTRADE = 'cross-trade'
}

export enum RuleSet {
  ON_START = 'onStart',
  BOOKING = 'booking',
  B13A = 'b13a'
}

export function getTradeRoute(customer: CustomerDetails, booking: Booking) : TradeRoute | null {

  switch (true) {
    case booking.origin.value === booking.destination.value:
      return  TradeRoute.DOMESTIC;
    case booking.origin.value !== booking.destination.value && booking.origin.value === customer.customerCountryCode:
      return  TradeRoute.EXPORT
    case booking.origin.value !== booking.destination.value && booking.destination.value === customer.customerCountryCode:
      return  TradeRoute.IMPORT
    case booking.origin.value !== booking.destination.value && (
      booking.origin.value !== customer.customerCountryCode && booking.destination.value !== customer.customerCountryCode
    ):
      return  TradeRoute.CROSSTRADE
  }
  return null;
}

export function isIntraEu(booking: Booking) {
  return booking.destination.tradeBloc === "EUCU" && booking.origin.tradeBloc === "EUCU"
}

export function isNorthernIrelandAddress(countryCode?: string, postcode?: string) {
  return countryCode === 'GB'
    && typeof postcode === 'string'
    && postcode.replace(/\s+/g, '').toUpperCase().startsWith('BT');
}

/**
 * Mirrors the pidApplicableEU booking rule (spec 013): a non-EU origin that is not Northern
 * Ireland, an EU destination and goods. RulesEngineUtilities.test.ts pins the two together.
 * At 'search' a GB origin with no postcode yet counts as a lane; Details re-checks.
 * The €150 value test is separate: isWithinPidValueLimit.
 */
export function isPidApplicableRoute(booking: Booking, stage: 'search' | 'details') {
  const postcode = stage === 'search'
    ? booking.originPostalCode
    : booking.shipperDetails?.postalCode || booking.originPostalCode;

  return booking.origin?.tradeBloc !== 'EUCU'
    && !isNorthernIrelandAddress(booking.origin?.value, postcode)
    && booking.destination?.tradeBloc === 'EUCU'
    && booking.shipmentType === ShipmentType.NON_DOCS;
}

export const PID_VALUE_LIMIT_EUR = 150;

// Ties a converted euro value to the value and currency it came from, so a result for an earlier value or booking is
// never used.
export function pidEurValueSource(booking: Booking) {
  return `${booking.preferredCurrency?.value}:${booking.totalShipmentValue}`;
}

/**
 * Mirrors the eurShipmentValue condition of the pidApplicableEU rule (spec 013): the shipment value in euros is at
 * most €150, whatever currency the customer entered. Euro values are compared as entered; others use the value
 * converted by usePidEurShipmentValue. When the euro value is not known, the result is unknownIsWithin.
 */
export function isWithinPidValueLimit(
  booking: Booking,
  eurValue: PidEurShipmentValue | null | undefined,
  unknownIsWithin: boolean
) {
  let eur: number | null = null;
  if (booking.totalShipmentValue !== '') {
    eur = booking.preferredCurrency?.value === 'EUR'
      ? +booking.totalShipmentValue
      : eurValue?.source === pidEurValueSource(booking) ? eurValue.eur : null;
  }
  return eur !== null && Number.isFinite(eur) ? eur <= PID_VALUE_LIMIT_EUR : unknownIsWithin;
}

export function isB2C(taxNumbers: { type: string, value: string }[] = []) {
  return !taxNumbers?.some(taxNumber =>
    (taxNumber.type === 'VAT' || taxNumber.type === 'EORI') && !!taxNumber.value?.trim()
  );
}

export function getPidClass(
  booking: Booking,
  customRules: { rule: string, value: any }[] | undefined,
  stage: 'search' | 'details',
  eurValue?: PidEurShipmentValue | null
): PidClass {
  // At Details the rule result decides when present. The route check is also required, because
  // rules run once when Details mounts (a BT postcode may be entered later) and customRules can
  // still hold a previous booking's results while the new ones load.
  const ruleAbsent = !customRules?.some(customRule => customRule.rule === 'pidApplicableEU');
  const ruleAllows = stage === 'search' || ruleAbsent || isRuleTrue(customRules, 'pidApplicableEU');
  // The rule applies the converted value itself. Without the rule, an unknown euro value is a lane
  // only for the Search advisory; on Details it is not applicable (spec 014 FR-001a).
  const lane = ruleAllows
    && isPidApplicableRoute(booking, stage)
    && isWithinPidValueLimit(booking, eurValue, !(stage === 'details' && ruleAbsent));

  if (!lane) return 'NOT_APPLICABLE';
  return isB2C(booking.consigneeDetails?.taxNumbers) ? 'B2C' : 'B2B';
}

async function callRulesEngine(ruleSet: String, customer: CustomerDetails, eventData: any, config: ApiConfig) {
  const url = config.headers['IdToken'] ? `${process.env.REACT_APP_NEBE_API_URL}/white-label/indirect/v1/rules` : `${process.env.REACT_APP_NEBE_API_URL}/v3/rules/run-engine`;
  return axios.post(
    url,
    {
      ruleSet: ruleSet,
      customer: customer,
      eventData: eventData
    },
    config
  ).then((response: any) => {
    return response.data["results"];
  }, (error: any) => {
    console.error('Rules engine error', error.message, { stack: error.stack });
  })
}

const PID_RULES = ['pidApplicableEU', 'pidHardBlock'];

export async function handleRules(ruleSet: RuleSet, customer: CustomerDetails, eventData: any, dispatch: AppDispatch, apiConfig: ApiConfig, currentRules?: { rule: string, value: any }[]) {
  try {
    let results = await callRulesEngine(ruleSet, customer, eventData, apiConfig);
    // The b13a rule set replaces the list on Confirm; keep the PID results from Details.
    if (ruleSet === RuleSet.B13A && currentRules) {
      const pidResults = currentRules.filter(customRule => PID_RULES.includes(customRule.rule));
      results = [...(results ?? []), ...pidResults];
    }
    dispatch(appDataActions.setCustomRules(results));
  } catch (error: any) {
    console.error(`Error handling ${ruleSet} rules: ${error.message}`, { stack: error.stack });
  }
}


export function getRule(rules: { rule: string, value: any }[], rule: string) {
  const result = rules?.find((customRule: any) => customRule.rule === rule);
  return result?.value ?? null;
}

// Rule values are stored as strings, so "false" must not read as true.
export function isRuleTrue(rules: { rule: string, value: any }[] | undefined, rule: string) {
  const value = rules ? getRule(rules, rule) : null;
  return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');
}